import { Tags } from "exifreader";
import {
  BrianImageMetadata,
  BrianImageMetadataImage,
  BrianImageMetadataOpticalAdapter,
  BrianImageMetadataVideo
} from "../../_objects/metadata/brian-image-metadata";
import { MarkItem } from "../../_objects/mark";
import { TitleItem } from "../../_objects/title";
import { MARK_DISABLED_COLOR, SCOPE_MODEL } from "../../app.constants";

/** Longest image comment ViSOL stores (edit dialog and server column); Merry allows 4096 characters. */
const COMMENT_MAX_LENGTH = 1000;

/** A tag as Merry serializes it, in the XMP "tags" property and in each bookmark (Merry tags-registry.service.ts). */
export interface MerryTag {
  id?: string;
  /**
   * Hex code points joined with "-" ("42" is "B"). Tags Merry imported from ViSOL's MARK_SET.dat carry the mark code
   * here, tags imported from TITLE.dat have none, and tags created in Merry carry the symbol the user picked (an emoji,
   * for example). A symbol alone does not make a tag a ViSOL mark: see mapTags.
   */
  symbol?: string;
  label?: string;
  color?: string;
}

interface MerryAdaptor {
  id?: string;
  modelNumber?: string;
  serialNumber?: string;
}

/**
 * The tip adapter Merry writes as JSON in the XMP "adapter" tag. Builds since MERRY-2467 (2026-09-21) nest it in
 * `detectedAdaptor`; earlier builds put `modelNumber` and `serialNumber` at the root.
 */
interface MerryAdapterTag extends MerryAdaptor {
  detectedAdaptor?: MerryAdaptor;
}

/** The Brian-shaped metadata of a Merry capture, carrying Merry's measurable verdict (see MerryMeasurable). */
export interface MerryBrianMetadata extends BrianImageMetadata {
  isMeasurable?: boolean;
  /** Merry tag labels the tag list has no tag for (see MerryUploadService), listed in the edit dialog. */
  unmatchedTags?: string[];
}

/** What MerryUploadService stored with the image at upload, kept when the EXIF wrapper's metadata replaces the stored one. */
export interface MerryUploadMetadata {
  title?: string | null;
  tags?: string[] | null;
  unmatchedTags?: string[] | null;
}

/** What Merry's tags map to in ViSOL (MerryImageMetadata.mapTags). */
export interface MerryTagMapping {
  /** The enabled ViSOL mark of the first Merry tag that is one; null when no tag is. */
  mark: MarkItem | null;
  /** The ViSOL title (in the settings' spelling) of the first Merry tag that is one; null when no tag is. */
  title: string | null;
  /** The labels of the tags that are neither a mark nor a title, the ones ViSOL tags are matched to (TagItems): each once, in Merry's order. */
  tagLabels: string[];
}

/**
 * Merry images (EXIF model "IPLEX ONE") keep their capture data as JSON in XMP tags instead of the Brian UserComment
 * block, so the EXIF wrapper returns them as plain images. This fills the Brian metadata (tip adapter for the properties,
 * and Merry's own measurable verdict that MeasurementMetaDataService reads back), so Merry images are handled as Brian
 * images, and maps Merry's tags and comments to the ViSOL mark, title and comment (MerryUploadService applies them at
 * upload). MerryImageMetadata.cs in the data management API is the counterpart for the web and the online modes.
 */
export class MerryImageMetadata {
  /** Case-insensitive: some captures carry "IPLEX One". */
  static isMerryModel(scopeModel: string | null | undefined): boolean {
    return scopeModel?.trim().toUpperCase() === SCOPE_MODEL.MERRY;
  }

  /**
   * Adds the Merry capture fields to the metadata the EXIF wrapper returned (mark, title, tags):
   * - opticalAdapter: model number (or id when missing) and serial number of the detected tip adapter
   * - image.printScreen: 1 when any print-screen field (date, title, tags, logo) is on, as Merry then burns it into the image
   * - video.zoom: always "1", Merry stores no zoom and always captures the full frame
   * - isMeasurable: Merry's verdict from the file (MerryMeasurable). Merry's adapter and print screen tags are not reliable
   *   enough to decide it (the adapter model number is sometimes missing, and a burned-in print screen leaves the capture
   *   measurable), so this is what MeasurementMetaDataService uses for Merry images.
   * `upload` replaces the wrapper's title, tags and unmatched tags: they are what Merry's tags were mapped to at upload
   * (see findTitle and tagLabels).
   */
  static toBrianMetadata(tags: Tags, wrapperMetadata: string | null, upload: MerryUploadMetadata | null, isMeasurable: boolean): string {
    const metadata: MerryBrianMetadata = MerryImageMetadata.parse<MerryBrianMetadata>(wrapperMetadata) ?? new BrianImageMetadata();
    if (upload?.title) {
      metadata.title = upload.title;
    }
    if (upload?.tags?.length) {
      metadata.tags = upload.tags;
    }
    if (upload?.unmatchedTags?.length) {
      metadata.unmatchedTags = upload.unmatchedTags;
    }
    const adapterTag = MerryImageMetadata.parse<MerryAdapterTag>(MerryImageMetadata.tagText(tags, "adapter"));
    const adaptor = adapterTag?.detectedAdaptor ?? adapterTag;
    const printScreenFields = MerryImageMetadata.parse<Record<string, unknown>>(MerryImageMetadata.tagText(tags, "printScreen")) ?? {};

    metadata.opticalAdapter = Object.assign(new BrianImageMetadataOpticalAdapter(), {
      name: adaptor?.modelNumber ?? adaptor?.id ?? "",
      adapterSetNo: adaptor?.serialNumber ?? ""
    });
    metadata.image = Object.assign(new BrianImageMetadataImage(), {
      printScreen: Object.values(printScreenFields).some((isOn) => isOn === true) ? 1 : 0
    });
    metadata.video = Object.assign(new BrianImageMetadataVideo(), { zoom: "1" });
    metadata.isMeasurable = isMeasurable;

    return JSON.stringify(metadata);
  }

  /** Merry's verdict stored with the metadata by toBrianMetadata; false for metadata stored before it was recorded. */
  static isStoredMeasurable(metadataJson: string | null | undefined): boolean {
    return MerryImageMetadata.parse<MerryBrianMetadata>(metadataJson)?.isMeasurable === true;
  }

  /** The unmatched tags stored with the metadata (see MerryUploadService); undefined when there are none. */
  static storedUnmatchedTags(metadataJson: string | null | undefined): string[] | undefined {
    const unmatchedTags = MerryImageMetadata.parse<MerryBrianMetadata>(metadataJson)?.unmatchedTags;
    const labels = Array.isArray(unmatchedTags) ? unmatchedTags.filter((label) => typeof label === "string") : [];
    return labels.length ? labels : undefined;
  }

  /**
   * Maps Merry's tags to the ViSOL mark, title and tags, each tag to one of them, in Merry's order:
   * - a tag is a mark when a mark in the ViSOL settings has its code (the symbol) and label. The first one that is enabled
   *   is applied; the other marks (a second one, "No Mark", a disabled mark) are dropped, as an image has one mark.
   * - a tag is a title when its label is in the ViSOL title settings. The first one is applied, the others are dropped.
   * - every other tag is a ViSOL tag, whatever its symbol: the tags Merry users create, and the tags of marks ViSOL no
   *   longer has (renamed, deleted), so that none of them is lost. Each label once.
   */
  static mapTags(tagsJson: unknown, markItems: MarkItem[], titleItems: TitleItem[]): MerryTagMapping {
    const mapping: MerryTagMapping = { mark: null, title: null, tagLabels: [] };
    for (const tag of MerryImageMetadata.parseTags(tagsJson)) {
      const mark = MerryImageMetadata.markOf(tag, markItems);
      if (mark) {
        if (!mapping.mark && mark.identifier > 0 && mark.color !== MARK_DISABLED_COLOR) {
          mapping.mark = mark;
        }
        continue;
      }

      const label = tag.label?.trim() ?? "";
      const normalized = MerryImageMetadata.normalize(label);
      if (!label) {
        continue;
      }
      const title = titleItems.find((item) => MerryImageMetadata.normalize(item.identifier) === normalized);
      if (title) {
        mapping.title ??= title.identifier;
      } else if (!mapping.tagLabels.some((known) => MerryImageMetadata.normalize(known) === normalized)) {
        mapping.tagLabels.push(label);
      }
    }
    return mapping;
  }

  /** The mark of the ViSOL settings with the tag's code (its symbol, see MerryTag) and label, enabled or not; null when there is none. */
  private static markOf(tag: MerryTag, markItems: MarkItem[]): MarkItem | null {
    if (!tag.symbol) {
      return null;
    }
    const code = MerryImageMetadata.decodeSymbol(tag.symbol).trim().toUpperCase();
    if (!code) {
      return null;
    }
    const label = MerryImageMetadata.normalize(tag.label);
    return markItems.find((item) => item.code?.trim().toUpperCase() === code && MerryImageMetadata.normalize(item.label) === label) ?? null;
  }

  /** Merry writes newlines as a literal "\n" in its comments; ViSOL keeps at most COMMENT_MAX_LENGTH characters. */
  static toComment(text: string | null | undefined): string | null {
    const comment = text?.replace(/\\n/g, "\n").trim();
    return comment ? comment.slice(0, COMMENT_MAX_LENGTH) : null;
  }

  /** Merry tags from their JSON text, or from an already parsed array. */
  static parseTags(tags: unknown): MerryTag[] {
    const parsed = typeof tags === "string" ? MerryImageMetadata.parse<unknown>(tags) : tags;
    return Array.isArray(parsed) ? parsed.filter((tag): tag is MerryTag => !!tag && typeof tag === "object") : [];
  }

  private static decodeSymbol(symbol: string): string {
    try {
      return symbol
        .split("-")
        .map((codePoint) => String.fromCodePoint(parseInt(codePoint, 16)))
        .join("");
    } catch {
      return "";
    }
  }

  private static normalize(text: string | null | undefined): string {
    return text?.trim().toLowerCase() ?? "";
  }

  private static tagText(tags: Tags, name: string): string | undefined {
    const description = (tags?.[name] as { description?: unknown } | undefined)?.description;
    return typeof description === "string" ? description : undefined;
  }

  private static parse<T>(json: string | null | undefined): T | null {
    if (!json) {
      return null;
    }
    try {
      return JSON.parse(json) as T;
    } catch {
      return null;
    }
  }
}
